import { mat4, vec3 } from "gl-matrix";
import { loadShader } from "../gl";
import * as fs from "fs";
import { LoadedFont } from "../loader";

export class TextShader {
  constructor(
    public readonly gl: WebGL2RenderingContext,
    private readonly program: WebGLProgram,
    private readonly resolutionLocation: WebGLUniformLocation,
    private readonly textureLocation: WebGLUniformLocation,
    private readonly transformLocation: WebGLUniformLocation,
    private readonly nowLocation: WebGLUniformLocation,
    private readonly opacityLocation: WebGLUniformLocation,
    private readonly wavinessLocation: WebGLUniformLocation,
    private readonly distortionLocation: WebGLUniformLocation,
    private readonly fontTypeLocation: WebGLUniformLocation,
    private readonly textColorLocation: WebGLUniformLocation,
  ) {}

  public static create(gl: WebGL2RenderingContext): TextShader {
    const textSdfProgram = loadShader(
      gl,
      fs.readFileSync(__dirname + "/text.vert", "utf8"),
      fs.readFileSync(__dirname + "/text.frag", "utf8"),
      "Text SDF",
    );
    const textureLocation = gl.getUniformLocation(textSdfProgram, "texture")!;
    const textResolutionLocation = gl.getUniformLocation(
      textSdfProgram,
      "resolution",
    )!;
    const textTransformLocation = gl.getUniformLocation(
      textSdfProgram,
      "transform",
    )!;
    const textNowLocation = gl.getUniformLocation(textSdfProgram, "now")!;

    const opacityLocation = gl.getUniformLocation(textSdfProgram, "opacity")!;

    const wavinessLocation = gl.getUniformLocation(textSdfProgram, "waviness")!;

    const distortionLocation = gl.getUniformLocation(
      textSdfProgram,
      "distortion",
    )!;

    const fontTypeLocation = gl.getUniformLocation(textSdfProgram, "fontType")!;

    const textColorLocation = gl.getUniformLocation(
      textSdfProgram,
      "textColor",
    )!;

    return new TextShader(
      gl,
      textSdfProgram,
      textResolutionLocation,
      textureLocation,
      textTransformLocation,
      textNowLocation,
      opacityLocation,
      wavinessLocation,
      distortionLocation,
      fontTypeLocation,
      textColorLocation,
    );
  }

  public use({
    now,
    resolution,
    transform,
    opacity,
    waviness = 0,
    distortion = 0,
    font,
    color = [1, 1, 1] as vec3,
  }: {
    now: number;
    resolution: [number, number];
    transform: mat4;
    opacity: number;
    waviness?: number;
    distortion?: number;
    font: LoadedFont;
    color?: vec3;
  }) {
    this.gl.useProgram(this.program);
    this.gl.activeTexture(this.gl.TEXTURE0);

    this.gl.uniform1i(this.textureLocation, 0);
    this.gl.uniform2fv(this.resolutionLocation, resolution);
    this.gl.uniformMatrix4fv(this.transformLocation, false, transform);
    this.gl.uniform1f(this.nowLocation, now);
    this.gl.uniform1f(this.opacityLocation, opacity);
    this.gl.uniform1f(this.wavinessLocation, waviness);
    this.gl.uniform1f(this.distortionLocation, distortion);
    this.gl.uniform1ui(this.fontTypeLocation, font.id);
    this.gl.uniform3fv(this.textColorLocation, color);
  }

  public destroy() {
    this.gl.deleteProgram(this.program);
  }
}
